import React, { useState, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Rocket, MessageSquare, FileSearch, Crosshair, LucideIcon } from 'lucide-react';

// Easy to update when moving to different domain
const VIEWPOINT_URL = "/login";

interface ProductCardProps {
  title: string;
  description: string;
  status: 'available' | 'lab' | 'dev';
  icon: LucideIcon;
  link: string | null;
  featured?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ title, description, status, icon: Icon, link, featured = false }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`relative rounded-2xl p-8 transition-all duration-300 ${
        featured
          ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white shadow-2xl'
          : 'bg-white dark:bg-purple-950/50 border-2 border-stone-200 dark:border-purple-500/20 hover:border-blue-400 dark:hover:border-purple-400'
      } ${isHovered ? 'transform -translate-y-2' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {status && (
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-4 ${
          featured
            ? 'bg-white/20 text-white'
            : 'bg-blue-50 dark:bg-purple-500/20 text-blue-600 dark:text-purple-300'
        }`}>
          {status === 'available' && <Sparkles className="w-3 h-3" />}
          {status === 'lab' && <span className="text-xs">🧪</span>}
          {status === 'dev' && <span className="text-xs">⚡</span>}
          {status === 'available' ? 'Available Now' : status === 'lab' ? 'In The Lab' : 'In Development'}
        </div>
      )}

      <div className="flex items-start gap-4 mb-4">
        <div className={`p-3 rounded-xl ${featured ? 'bg-white/20' : 'bg-blue-50 dark:bg-purple-500/20'}`}>
          <Icon className={`w-8 h-8 ${featured ? 'text-white' : 'text-blue-600 dark:text-purple-400'}`} />
        </div>
        <div>
          <h3 className={`text-2xl font-bold mb-2 ${featured ? 'text-white' : 'text-stone-900 dark:text-white'}`}>
            {title}
          </h3>
          <p className={`text-sm ${featured ? 'text-white/90' : 'text-stone-600 dark:text-purple-300'}`}>
            {description}
          </p>
        </div>
      </div>

      {link ? (
        <a
          href={link}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            featured
              ? 'bg-white text-blue-600 hover:bg-blue-50'
              : 'bg-blue-600 dark:bg-purple-600 text-white hover:bg-blue-700 dark:hover:bg-purple-700'
          }`}
        >
          {featured ? 'Launch ViewPoint' : 'Learn More'}
          <ArrowRight className="w-4 h-4" />
        </a>
      ) : (
        <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${
          'bg-stone-100 dark:bg-black/20 text-stone-400 dark:text-purple-400/50 cursor-not-allowed'
        }`}>
          Coming Soon
        </div>
      )}
    </div>
  );
};

export default function LandingPage() {
  // DigitalAIM landing is a separate brand: pin the document to dark while this page
  // is mounted, then restore the user's app theme on leave. This keeps the landing in
  // its original "standard dark" look regardless of the app's light/dark toggle.
  useLayoutEffect(() => {
    const html = document.documentElement;
    const force = () => { html.setAttribute('data-theme', 'dark'); html.classList.add('dark'); };
    force();
    // Re-assert once after mount effects to win over ThemeProvider's own mount
    // effect on a hard reload at "/" (client-side navigation is already covered).
    const t = window.setTimeout(force, 0);
    return () => {
      window.clearTimeout(t);
      // Restore the document to the user's saved app theme on leaving the landing.
      const saved = (localStorage.getItem('theme') as 'dark' | 'light') || 'dark';
      html.setAttribute('data-theme', saved);
      html.classList.toggle('dark', saved === 'dark');
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Navigation */}
      <nav className="border-b border-stone-200 dark:border-purple-500/20 bg-white/80 dark:bg-black/30 backdrop-blur-md sticky top-0 z-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Crosshair className="text-white w-6 h-6" />
            </div>
            <span className="text-xl font-bold text-stone-900 dark:text-white">DigitalAIM</span>
          </div>
          <a
            href={VIEWPOINT_URL}
            className="px-6 py-2 text-sm font-medium text-white dark:text-white bg-blue-600 hover:bg-blue-700 dark:bg-purple-600 dark:hover:bg-purple-700 rounded-lg transition-all shadow-sm hover:shadow-md"
          >
            Launch ViewPoint
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-20 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-purple-500/20 text-blue-600 dark:text-purple-300 text-sm font-medium mb-6">
          <Sparkles className="w-4 h-4" />
          Your Digital Team, Powered by AI
        </div>
        <h1 className="text-5xl md:text-6xl font-bold text-stone-900 dark:text-white mb-6 leading-tight">
          Build like a startup,<br />
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            compete like an enterprise
          </span>
        </h1>
        <p className="text-xl text-stone-600 dark:text-purple-300 max-w-2xl mx-auto mb-2">
          From solo entrepreneurs to growing teams, we build intelligent solutions that give ambitious founders the power to compete with giants, without the headcount. Work smarter, faster, and more efficiently.
        </p>
        <p className="text-xl text-stone-700 dark:text-purple-200 max-w-2xl mx-auto mb-8 font-semibold">
          Meet your digital team.
        </p>
        <a
          href={VIEWPOINT_URL}
          className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 dark:bg-purple-600 text-white hover:bg-blue-700 dark:hover:bg-purple-700 rounded-lg font-medium text-lg transition-all shadow-lg hover:shadow-xl"
        >
          Get Started with ViewPoint
          <ArrowRight className="w-5 h-5" />
        </a>
      </section>

      {/* Products Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-stone-900 dark:text-white mb-4">Our Products</h2>
          <p className="text-lg text-stone-600 dark:text-purple-300 max-w-2xl mx-auto">
            Build your digital team with powerful solutions designed to help you reach your full potential.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* ViewPoint - Featured Product */}
          <ProductCard
            title="ViewPoint"
            description="Your AI document analyst. Extract insights, analyze invoices, and organize information instantly. The team member you need to scale - without adding headcount. Built for ambitious teams doing more with less."
            status="available"
            icon={FileSearch}
            link={VIEWPOINT_URL}
            featured={true}
          />

          {/* Placeholder Product 1 - Development Workflow */}
          <ProductCard
            title="Project Velocity"
            description="Your AI teammate that turns conversations into action. From meeting notes to deployment: imagine having someone who handles the whole journey."
            status="dev"
            icon={Rocket}
            link={null}
          />

          {/* Placeholder Product 2 - Social Media Manager */}
          <ProductCard
            title="Echo"
            description="A voice that never sleeps. Your always-on team member for the social world. Constantly learning, always engaging, perfectly on-brand."
            status="lab"
            icon={MessageSquare}
            link={null}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-purple-600 dark:to-purple-800 rounded-3xl p-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to scale without limits?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join ambitious founders and growing teams using ViewPoint to compete with giants - without the headcount
          </p>
          <a
            href={VIEWPOINT_URL}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white dark:bg-white text-blue-600 dark:text-purple-600 rounded-lg font-medium text-lg hover:bg-blue-50 dark:hover:bg-stone-100 transition-all shadow-lg"
          >
            Get Started with ViewPoint
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 dark:border-purple-500/20 bg-stone-50 dark:bg-black/20">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">A</span>
                  </div>
                  <span className="font-bold text-stone-900 dark:text-white">DigitalAIM</span>
                </div>
                <p className="text-sm text-stone-600 dark:text-purple-300">
                  Empowering individuals and teams with big ambitions.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-stone-900 dark:text-white mb-3">Products</h4>
                <ul className="space-y-2 text-sm text-stone-600 dark:text-purple-300">
                  <li><a href={VIEWPOINT_URL} className="hover:text-blue-600 dark:hover:text-purple-400">ViewPoint</a></li>
                  <li><span className="text-stone-400 dark:text-purple-400/50">More coming soon...</span></li>
                </ul>
              </div>

              {/* These were six href="#" placeholders that all jumped to the top
                  of the page. Only the ones with a real destination remain. */}
              <div>
                <h4 className="font-semibold text-stone-900 dark:text-white mb-3">Company</h4>
                <ul className="space-y-2 text-sm text-stone-600 dark:text-purple-300">
                  <li>
                    <a href="mailto:info@vpoint.app" className="hover:text-blue-600 dark:hover:text-purple-400">Contact</a>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-stone-900 dark:text-white mb-3">Support</h4>
                <ul className="space-y-2 text-sm text-stone-600 dark:text-purple-300">
                  <li>
                    <Link to="/guide" className="hover:text-blue-600 dark:hover:text-purple-400">User Guide</Link>
                  </li>
                  <li>
                    <a href="mailto:info@vpoint.app" className="hover:text-blue-600 dark:hover:text-purple-400">Help</a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t border-stone-200 dark:border-purple-500/20 mt-8 pt-8 text-center text-sm text-stone-600 dark:text-purple-300">
              <p>&copy; 2025 DigitalAIM. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
